import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Home, ArrowLeft } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';

const CadastrarProjeto = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    tipo: '',
    regiao: '',
    descricao: '',
    objetivos: '',
    resultadosEsperados: '',
    email: '',
    telefone: '',
    responsavel: ''
  });

  const tipos = [
    "Mitigação", 
    "Conservação", 
    "Educação", 
    "Tecnologia", 
    "Infraestrutura", 
    "Energia Renovável",
    "Reflorestamento",
    "Restauração"
  ];

  const regioes = [
    "Norte", 
    "Nordeste", 
    "Centro-Oeste", 
    "Sudeste", 
    "Sul", 
    "Nacional"
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Simulação de envio do formulário
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      console.log('Dados enviados:', formData);
      toast({
        title: 'Formulário enviado com sucesso!',
        description: 'Seu projeto foi registrado e será analisado pela nossa equipe.',
        variant: 'default',
      });
      
      // Redirecionar para a página inicial ou de confirmação
      navigate('/all-projects');
    } catch (error) {
      console.error('Erro ao enviar formulário:', error);
      toast({
        title: 'Erro ao enviar formulário',
        description: 'Ocorreu um erro ao enviar seu projeto. Por favor, tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm py-4 px-6">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-[#0e7cb4]"
            >
              <ArrowLeft className="h-5 w-5 mr-1" />
              Voltar
            </button>
            <h1 className="text-2xl font-bold text-gray-800">Registrar Novo Projeto</h1>
          </div>
          <button 
            className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white px-4 py-2 rounded flex items-center justify-center"
            onClick={() => navigate('/dashboard')}
          >
            <Home className="h-5 w-5" />
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow py-8 px-6">
        <div className="container mx-auto max-w-3xl">
          <Card className="shadow-md">
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold border-b pb-2">Informações do Projeto</h2>
                  
                  <div>
                    <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-1">
                      Nome do Projeto *
                    </label>
                    <Input
                      id="nome"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      required
                      placeholder="Digite o nome do projeto"
                      className="w-full"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="tipo" className="block text-sm font-medium text-gray-700 mb-1">
                        Tipo de Projeto *
                      </label>
                      <Select
                        value={formData.tipo}
                        onValueChange={(value) => handleSelectChange('tipo', value)}
                        required
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Selecione o tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          {tipos.map((tipo) => (
                            <SelectItem key={tipo} value={tipo}>
                              {tipo}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label htmlFor="regiao" className="block text-sm font-medium text-gray-700 mb-1">
                        Região *
                      </label>
                      <Select
                        value={formData.regiao}
                        onValueChange={(value) => handleSelectChange('regiao', value)}
                        required
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Selecione a região" />
                        </SelectTrigger>
                        <SelectContent>
                          {regioes.map((regiao) => (
                            <SelectItem key={regiao} value={regiao}>
                              {regiao}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="descricao" className="block text-sm font-medium text-gray-700 mb-1">
                      Descrição do Projeto *
                    </label>
                    <Textarea
                      id="descricao"
                      name="descricao"
                      value={formData.descricao}
                      onChange={handleChange}
                      required
                      placeholder="Descreva o projeto em detalhes"
                      className="w-full min-h-[120px]"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="objetivos" className="block text-sm font-medium text-gray-700 mb-1">
                      Objetivos *
                    </label>
                    <Textarea
                      id="objetivos"
                      name="objetivos"
                      value={formData.objetivos}
                      onChange={handleChange}
                      required
                      placeholder="Descreva os principais objetivos do projeto"
                      className="w-full min-h-[100px]"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="resultadosEsperados" className="block text-sm font-medium text-gray-700 mb-1">
                      Resultados Esperados *
                    </label>
                    <Textarea
                      id="resultadosEsperados"
                      name="resultadosEsperados"
                      value={formData.resultadosEsperados}
                      onChange={handleChange}
                      required
                      placeholder="Quais resultados você espera alcançar com este projeto?"
                      className="w-full min-h-[100px]"
                    />
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold border-b pb-2">Informações de Contato</h2>
                  
                  <div>
                    <label htmlFor="responsavel" className="block text-sm font-medium text-gray-700 mb-1">
                      Nome do Responsável *
                    </label>
                    <Input
                      id="responsavel"
                      name="responsavel"
                      value={formData.responsavel}
                      onChange={handleChange}
                      required
                      placeholder="Nome completo do responsável pelo projeto"
                      className="w-full"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        E-mail *
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="seu@email.com"
                        className="w-full"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="telefone" className="block text-sm font-medium text-gray-700 mb-1">
                        Telefone *
                      </label>
                      <Input
                        id="telefone"
                        name="telefone"
                        value={formData.telefone}
                        onChange={handleChange}
                        required
                        placeholder="(00) 00000-0000"
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex justify-end gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate(-1)}
                      className="border-gray-300"
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white px-6"
                      disabled={loading}
                    >
                      {loading ? 'Enviando...' : 'Registrar Projeto'}
                    </Button>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default CadastrarProjeto; 